import * as React from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { diffLines } from 'diff';

import styles from './rich_text.module.css';
import Copy from './copy.component';

// What pasting the diff should yield: the after-state. Only strips prefixes when
// every line is properly prefixed (+/-/space) — otherwise copy the text as-is.
const diffResultCode = (text: string) => {
  const lines = text.split('\n');
  const isUnified = lines.every(line => line.length === 0 || '+- '.includes(line[0]));
  if (!isUnified) return text;
  return lines.filter(line => !line.startsWith('-')).map(line => line.slice(1)).join('\n');
};

// Unified diff of a code change: removed lines red, added lines green, unchanged as context
export const DiffView = ({original, fixed}: {original: string, fixed: string}) => {
  const parts = diffLines(original, fixed);
  return (
    <pre className={styles.diff}>
      {parts.map((part, partIndex) => {
        const lineClass = part.added ? styles.diffAdded : part.removed ? styles.diffRemoved : styles.diffContext;
        const prefix = part.added ? '+' : part.removed ? '-' : ' ';
        return part.value.replace(/\n$/, '').split('\n').map((line, lineIndex) => (
          <div key={`${partIndex}-${lineIndex}`} className={lineClass}>{prefix} {line}</div>
        ));
      })}
    </pre>
  );
};

// A ```diff fenced block: colored by the +/- line prefixes the model wrote
const DiffBlock = ({text}: {text: string}) => (
  <pre className={styles.diff}>
    {text.split('\n').map((line, index) => {
      const lineClass = line.startsWith('+') ? styles.diffAdded : line.startsWith('-') ? styles.diffRemoved : styles.diffContext;
      return <div key={index} className={lineClass}>{line}</div>;
    })}
  </pre>
);

// Renders text with ``` fenced blocks: even parts are prose, odd parts are code (diff blocks colored)
export const MarkdownishText = ({text}: {text: string}) => {
  const parts = text.split('```');
  return (
    <React.Fragment>
      {parts.map((part, index) => {
        if (index % 2 === 0) {
          if (part.trim().length === 0) return null;
          return <div key={index} className={styles.prose}>{part.trim()}</div>;
        }
        // The first line of a fenced block may be the language tag
        const newLine = part.indexOf('\n');
        const firstLine = newLine >= 0 ? part.slice(0, newLine).trim() : '';
        const isLanguage = /^\w+$/.test(firstLine);
        const language = isLanguage ? firstLine : undefined;
        const code = (isLanguage && newLine >= 0 ? part.slice(newLine + 1) : part).trim();
        return (
          <div key={index} className={styles.codeWrap}>
            <div className={styles.copyCorner}>
              <Copy text={language === 'diff' ? diffResultCode(code) : code} />
            </div>
            {language === 'diff' ?
              <DiffBlock text={code} /> :
              <SyntaxHighlighter language={language} style={docco} className={styles.code}>
                {code}
              </SyntaxHighlighter>}
          </div>
        );
      })}
    </React.Fragment>
  );
};
